import React, { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import SimpleChatBox from './SimpleChatBox';

interface ChatParticipant {
  id: number;
  name: string;
  type: 'student' | 'counselor' | 'parent';
  email?: string;
  avatar?: string;
  phoneNumber?: string;
  partnerId?: number;
  sessionId?: string;
  is_phone_verify?: boolean;
  subscriptionPlan?: string;
  // Optional: used to scroll to a specific message when opening from search
  initialMessageId?: number;
}

interface ChatBoxManagerContextType {
  openChatBox: (participant: ChatParticipant) => void;
  closeChatBox: (participantId: number) => void;
  closeAllChatBoxes: () => void;
  isChatBoxOpen: (participantId: number) => boolean;
}

export const ChatBoxManagerContext = React.createContext<ChatBoxManagerContextType | undefined>(undefined);

export const ChatBoxManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [openChatBoxes, setOpenChatBoxes] = useState<ChatParticipant[]>([]);
  const location = useLocation();
  
  // Check if current route is a public page
  const isPublicPage = location.pathname.startsWith('/public');

  const openChatBox = useCallback((participant: ChatParticipant) => {
    setOpenChatBoxes((prev) => {
      // If already open, update its data (e.g. initialMessageId) but keep position
      if (prev.some((p) => p.id === participant.id)) {
        return prev.map((p) =>
          p.id === participant.id
            ? {
                ...p,
                ...participant,
              }
            : p
        );
      }

      // Add new chatbox (max 3)
      const newBoxes = [...prev, participant];
      // If more than 3, remove the first one
      if (newBoxes.length > 3) {
        return newBoxes.slice(1);
      }
      return newBoxes;
    });
  }, []);

  const closeChatBox = useCallback((participantId: number) => {
    setOpenChatBoxes((prev) => prev.filter((p) => p.id !== participantId));
  }, []);

  const closeAllChatBoxes = useCallback(() => {
    setOpenChatBoxes([]);
  }, []);

  const isChatBoxOpen = useCallback((participantId: number) => {
    return openChatBoxes.some((p) => p.id === participantId);
  }, [openChatBoxes]);

  return (
    <ChatBoxManagerContext.Provider
      value={{
        openChatBox,
        closeChatBox,
        closeAllChatBoxes,
        isChatBoxOpen,
      }}
    >
      {children}
      {/* Render chatboxes - only show on non-public pages */}
      {!isPublicPage &&
        openChatBoxes.map((participant, index) => {
          return (
            <SimpleChatBox
              key={participant.id}
              participant={{
                ...participant,
                isVerifiedPhoneNumber: participant?.is_phone_verify || false,
                phoneNumber: participant?.phoneNumber || '',
                email: participant?.email || '',
                studentId: participant?.id || 0,
                counselorId:
                  (participant.type === 'counselor' && participant?.partnerId) || undefined,
                parentId:
                  (participant.type === 'parent' && participant?.partnerId) || undefined,
                sessionId: participant.sessionId,
                subscriptionPlan: participant?.subscriptionPlan || '',
              }}
              initialScrollMessageId={participant.initialMessageId}
              onClose={() => closeChatBox(participant.id)}
              index={index}
            />
          );
        })}
    </ChatBoxManagerContext.Provider>
  );
};

export const useChatBoxManager = () => {
  const context = React.useContext(ChatBoxManagerContext);
  if (context === undefined) {
    throw new Error('useChatBoxManager must be used within a ChatBoxManagerProvider');
  }
  return context;
};

